/**
 * Central title utility — v1 pattern port.
 *
 * Provides `hasVisibleTitle` and `getTitleWithFallback` for consistent
 * empty-title handling across all object types (tasks, sections,
 * projects, lists, areas).
 *
 * Usage:
 *   const hasTitle = hasVisibleTitle(task.title)
 *   const display = getTitleWithFallback({ title: task.title, t, objectType: task.type })
 *   // → "My Task" or "New To-Do" (localized), with `hasTitle` for opacity control
 */

import type { TFunction } from "i18next"
import { ObjectType } from "@/db/schema"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TitleEntityType =
  | "task"
  | "section"
  | "project"
  | "list"
  | "area"

export interface TitleWithFallbackOptions {
  title?: string | null
  t: TFunction
  objectType?: ObjectType | null
  /** Explicit override — when set, takes precedence over objectType */
  fallback?: TitleEntityType
}

// ---------------------------------------------------------------------------
// Mappings
// ---------------------------------------------------------------------------

/** i18n keys returned for each entity type when the title is empty. */
const FALLBACK_KEYS: Record<TitleEntityType, string> = {
  task: "New To-Do",
  section: "New Section",
  project: "New Project",
  list: "New List",
  area: "New Area",
}

const OBJECT_TYPE_TO_ENTITY: Partial<Record<ObjectType, TitleEntityType>> = {
  [ObjectType.Task]: "task",
  [ObjectType.Checklist]: "task",
  [ObjectType.Subtask]: "task",
  [ObjectType.Section]: "section",
  [ObjectType.Project]: "project",
  [ObjectType.List]: "list",
  [ObjectType.Area]: "area",
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Returns true when the title contains at least one non-whitespace char. */
export const hasVisibleTitle = (value?: string | null): boolean =>
  (value ?? "").trim().length > 0

/**
 * Returns the real title when present, or a localized fallback string
 * ("New To-Do", "New Section", …) when the title is empty/blank.
 *
 * Fallback resolution:
 * 1. Explicit `fallback` param if provided.
 * 2. Auto-detected from `objectType` via OBJECT_TYPE_TO_ENTITY mapping.
 * 3. Returns empty string if neither is available.
 */
export const getTitleWithFallback = ({
  title,
  t,
  objectType,
  fallback,
}: TitleWithFallbackOptions): string => {
  if (hasVisibleTitle(title)) {
    return title ?? ""
  }

  const entityType = fallback ?? (objectType != null ? OBJECT_TYPE_TO_ENTITY[objectType] : undefined)
  if (!entityType) {
    return ""
  }

  return t(FALLBACK_KEYS[entityType]) as string
}
